/**
 * lib/obs/log.mjs — canonical observability spine entry point
 *
 * PAUL Plan 92-01 / Milestone v22.0 — the fourth observability spine
 * alongside Vercel logs, git history, and Chrome DevTools. Per CARL
 * OBSERVABILITY rule 6 (CEO directive 2026-05-06): "No code gets done and
 * extended without a centralized log."
 *
 * Contract:
 *   - Strict mode: required fields (trace_id, source, module, step, status)
 *     must be present; missing → synchronous throw.
 *   - Lenient mode: missing required fields default to null. Used by
 *     thin-wrapper migrations of legacy helpers (logVoiceEvent, logAction)
 *     so existing callers continue producing the SAME row shape they
 *     produced pre-migration.
 *   - Always fire-and-forget (returns Promise that never rejects to caller).
 *   - PII payload sanitized BEFORE insert (audit M6 / AC-7) — no bypass.
 *   - Self-failure surfaces via console.error so Vercel logs see drops
 *     even when the table doesn't (audit S1).
 *   - Kill-switch via OBS_SPINE_DISABLED=1 (audit S4).
 *
 * Server: lazy-creates a service-role admin client when first called and
 *   process.env has SUPABASE_URL + SUPABASE_SERVICE_ROLE_KEY.
 * Client: caller must call setSupabaseClient(supabase) once at boot —
 *   src/lib/voice/voiceLogger.js and src/lib/observability.js do this on
 *   module load so logEvent has a client when invoked from the browser.
 */

const REQUIRED_STRICT = ['trace_id', 'source', 'module', 'step', 'status'];

// ---------------------------------------------------------------------------
// TICKET-795 — trace_id is a `uuid` COLUMN, and ten call-sites pass a label.
//
// `api/ops.js` ('cron.supplier_invoice_sweep', 'cron.lightrag_graph_health',
// 'cron.lightrag_write_sentinel', 'cron.invoice_document_backfill'),
// `api/invoice-intake.js` ('intake-<hash>', 'bm-<id>'), `storeInvoiceFile.js`
// and `cfo-invoice-export.js` all pass a human-readable string. Postgres
// rejected each INSERT with 22P02 and the row went to console.error instead of
// the spine — 46 occurrences in 24h, first seen 2026-06-16, discovered only
// once the Vercel connector could read runtime logs again (2026-09-07). The
// supplier-invoice cron sweep therefore had NO telemetry whatsoever: start, ok,
// warn and error alike were discarded, so a broken ingest and a quiet night
// produced the identical blank dashboard.
//
// Fixed HERE rather than at the ten call-sites, because the eleventh caller
// would reintroduce it. A label becomes a stable UUID (same label -> same uuid,
// so a cron still correlates across runs) and the label itself is preserved in
// metadata.trace_label so the row stays greppable by name.
// ---------------------------------------------------------------------------

const UUID_RX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Deterministic UUID from an arbitrary label.
 *
 * Deliberately NOT node:crypto — this module is isomorphic and also runs in the
 * browser bundle, so it cannot import a node builtin. Four FNV-1a passes with
 * different seeds give the 16 bytes; collision resistance is irrelevant when
 * the input space is a dozen hand-written cron names, and the goal is a
 * well-formed uuid that groups rows, not a security primitive.
 *
 * @param {string} label
 * @returns {string} RFC-shaped UUID (version nibble 8 = custom, per RFC 9562)
 */
export function toTraceUuid(label) {
  const s = String(label);
  let hex = '';
  for (let seed = 0; seed < 4; seed++) {
    let h = (2166136261 ^ Math.imul(seed, 0x9e3779b9)) >>> 0;
    for (let i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = Math.imul(h, 16777619) >>> 0;
    }
    hex += h.toString(16).padStart(8, '0');
  }
  const b = hex.split('');
  b[12] = '8';                                                   // version
  b[16] = ((parseInt(b[16], 16) & 0x3) | 0x8).toString(16);      // variant
  const h32 = b.join('');
  return `${h32.slice(0, 8)}-${h32.slice(8, 12)}-${h32.slice(12, 16)}-${h32.slice(16, 20)}-${h32.slice(20)}`;
}

const TRANSCRIPT_KEY_RX = /^(text|raw_text|canonical_text|transcript|message|content|prompt|response|user_text_preview|spoken_response_short|text_response_full)$/i;
const PATH_RX = /^(project-documents|task-photos|voice-uploads|task-attachments|invoices|material-photos)\//;
const NAME_KEY_RX = /^(deal_name|vessel_name|person_name|contact_name|company_name)$/i;

function sha256Prefix(s) {
  // Lightweight non-cryptographic hash — goal is "redacted but uniquely
  // identifiable across rows", not cryptographic strength. crypto.subtle is
  // async and not available in every test runtime; this 32-bit FNV-ish
  // sum is deterministic and adequate for sampling/grouping.
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  const u = h >>> 0;
  return u.toString(16).padStart(8, '0').slice(0, 8);
}

export function sanitizePayload(payload) {
  if (payload === null || payload === undefined) return payload;
  if (typeof payload !== 'object') return payload;
  if (Array.isArray(payload)) return payload.map(sanitizePayload);
  const out = {};
  for (const [k, v] of Object.entries(payload)) {
    if (typeof v === 'string' && v.length > 0 && TRANSCRIPT_KEY_RX.test(k)) {
      out[k] = { _redacted: true, length: v.length, sha256_prefix: sha256Prefix(v) };
      continue;
    }
    if (typeof v === 'string' && v.length > 0 && NAME_KEY_RX.test(k)) {
      const idKey = k.replace(/_name$/i, '_id');
      const entityId = (idKey !== k && payload[idKey]) ? payload[idKey] : null;
      out[k] = { _redacted: true, entity_id: entityId };
      continue;
    }
    if (typeof v === 'string' && PATH_RX.test(v)) {
      const basename = v.split('/').slice(-1)[0];
      out[k] = { _redacted: true, basename };
      continue;
    }
    if (v && typeof v === 'object') {
      out[k] = sanitizePayload(v);
      continue;
    }
    out[k] = v;
  }
  return out;
}

export function newTraceId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for old runtimes — shape is uuid-ish, uniqueness adequate for tests
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 14).padEnd(12, '0')}-4xxx-yxxx-xxxxxxxxxxxx`
    .replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
}

let _injectedClient = null;

/**
 * Set the supabase client logEvent should use. Client code calls this once
 * at module boot (src/lib/voice/voiceLogger.js, src/lib/observability.js).
 * Tests can call this to inject a mock; pass null to clear.
 */
export function setSupabaseClient(client) {
  _injectedClient = client;
}

let _serverClientPromise = null;

async function resolveSupabaseClient() {
  if (_injectedClient) return _injectedClient;
  // In Vitest with no caller-injected client, return null so logEvent no-ops
  // gracefully instead of trying to hit the stubbed http://test.local URL.
  // Tests that want to verify spine emits inject a mock via setSupabaseClient.
  if (typeof process !== 'undefined' && process?.env?.VITEST) return null;
  // Server fallback — only when env vars are present and not the test stub
  if (typeof process !== 'undefined' && process?.env?.SUPABASE_URL && process?.env?.SUPABASE_SERVICE_ROLE_KEY) {
    if (process.env.SUPABASE_URL === 'http://test.local') return null;
    if (!_serverClientPromise) {
      _serverClientPromise = (async () => {
        try {
          const mod = await import('@supabase/supabase-js');
          return mod.createClient(process.env.SUPABASE_URL, process.env.SUPABASE_SERVICE_ROLE_KEY, {
            auth: { persistSession: false },
          });
        } catch {
          return null;
        }
      })();
    }
    return _serverClientPromise;
  }
  return null;
}

function isKillSwitchOn() {
  if (typeof process !== 'undefined' && process?.env?.OBS_SPINE_DISABLED === '1') return true;
  if (typeof window !== 'undefined' && window.__OBS_SPINE_DISABLED__ === true) return true;
  return false;
}

/**
 * logEvent({ trace_id, source, module, step, status, ... }, options)
 *
 * options.mode = 'strict' (default) | 'lenient'
 *   strict — required fields enforced; missing throws synchronously
 *   lenient — used by thin-wrappers migrating legacy callers; missing → null
 */
/**
 * @returns {Promise<boolean>} TRUE only when the row was actually INSERTED.
 *   False = dropped (no client), rejected by Postgres, threw, or kill-switched.
 *   TICKET-371 (H2): this used to return undefined on every path, so callers that
 *   wanted to know "did my telemetry land?" could not tell a delivery from a silent
 *   drop — and the harness's own `outcome.emitted` health signal read true while
 *   nothing was being written. Callers may still ignore the value; it never throws.
 */
export async function logEvent(input, options = {}) {
  // Audit S4 / GAP-10: kill-switch FIRST so a hot env var disables emits
  // without a redeploy if the spine itself causes a regression.
  if (isKillSwitchOn()) return false;

  const mode = options.mode || 'strict';
  let payload = input || {};

  // TICKET-1349 class-kill: a strict call with no trace_id used to THROW, and the 77
  // callers that omit it all end in .catch(() => {}), so their rows were silently dropped
  // (module 'photos-describe': 0 rows ever; the CFO handlers: 0 rows until TICKET-1347).
  // Generate it here, ONCE for every caller, and flag it loud so "one trace_id per
  // operation" stays auditable (query metadata->>'trace_defaulted'). The other required
  // fields still throw: a missing step/status is a caller bug the tests must catch.
  let traceDefaulted = false;
  if (mode === 'strict' && (payload.trace_id === undefined || payload.trace_id === null || payload.trace_id === '')) {
    payload = { ...payload, trace_id: newTraceId() };
    traceDefaulted = true;
  }

  if (mode === 'strict') {
    for (const k of REQUIRED_STRICT) {
      const v = payload[k];
      if (v === undefined || v === null || v === '') {
        throw new Error(
          `[logEvent] strict mode requires "${k}" — got ${JSON.stringify(v)}. ` +
          `Use { mode: 'lenient' } from thin wrappers, or supply the field.`,
        );
      }
    }
  }

  const client = await resolveSupabaseClient();
  if (!client) {
    // Silent in Vitest (tests can verify by injecting a mock). Otherwise
    // surface to Vercel logs so a missed setSupabaseClient is visible.
    const isTest = typeof process !== 'undefined' && process?.env?.VITEST;
    if (!isTest && typeof console !== 'undefined') {
      console.warn('[logEvent] no supabase client available, dropping row', {
        module: payload.module ?? null,
        step: payload.step ?? null,
        status: payload.status ?? null,
      });
    }
    return false;
  }

  // Two metadata contracts — chosen by which field the caller used:
  //   - `payload` → canonical shape (wrap with step/status/timestamp).
  //     voiceLogger and other thin-wrapper callers use this.
  //   - `metadata` → legacy passthrough (use exactly as given).
  //     observability.js _log() and direct callers use this so existing
  //     dashboards don't see step/status injected.
  const useCanonicalWrap = payload.payload !== undefined;
  const rawMeta = sanitizePayload(payload.payload ?? payload.metadata ?? {}) || {};

  // TICKET-795: a non-uuid trace_id would make Postgres reject the whole row.
  const rawTrace = payload.trace_id ?? null;
  const traceIsLabel = typeof rawTrace === 'string' && rawTrace !== '' && !UUID_RX.test(rawTrace);
  // A caller's own trace_label always wins -- coercion adds context, never overwrites it.
  const traceMeta = {
    ...(traceIsLabel && rawMeta.trace_label === undefined ? { trace_label: rawTrace } : {}),
    ...(traceDefaulted ? { trace_defaulted: true } : {}),
  };
  const errorMessage = payload.error_message ?? null;
  const explicitMessage = payload.message ?? null;
  const legacyTimestamp = (rawMeta && rawMeta.timestamp) || new Date().toISOString();

  // Compose the row. Canonical columns map 1:1 to the new schema. Legacy
  // columns (view_name, error_type, message, metadata) keep producing
  // shapes compatible with the existing 140+ direct callers.
  const row = {
    // Canonical (new) columns
    trace_id:    traceIsLabel ? toTraceUuid(rawTrace) : rawTrace,
    session_id:  payload.session_id ?? null,
    source:      payload.source ?? null,
    page:        payload.page ?? null,
    scope_type:  payload.scope_type ?? null,
    scope_id:    payload.scope_id ?? null,
    module:      payload.module ?? null,
    step:        payload.step ?? null,
    status:      payload.status ?? null,
    duration_ms: payload.duration_ms ?? null,
    error_code:  payload.error_code ?? null,

    // Pre-existing columns (preserved for legacy compatibility)
    user_id:     payload.user_id ?? null,
    error_type:  payload.error_type
                  ?? (payload.module && payload.status
                        ? (payload.status === 'error' ? `${payload.module}_error` : `${payload.module}_event`)
                        : null),
    message:     explicitMessage
                  ?? (errorMessage != null
                        ? `[${payload.step ?? ''}] ${errorMessage}`
                        : (payload.step
                              ? `[${payload.step}] ${payload.status ?? ''}`.trim()
                              : null)),
    metadata:    useCanonicalWrap
      ? { step: payload.step ?? null, status: payload.status ?? null, timestamp: legacyTimestamp, ...rawMeta, ...traceMeta }
      : { ...rawMeta, ...traceMeta },
    view_name:   payload.view_name ?? null,
    app_version: payload.app_version ?? null,
    stack:       payload.stack ?? null,
  };

  // Strip nulls only from the canonical columns where the schema expects
  // null-as-absent semantics — leave legacy columns alone so direct-shape
  // comparisons against pre-migration rows still match. This is a no-op
  // because Supabase accepts nulls; kept here for future tuning.

  try {
    const { error } = await client.from('app_error_logs').insert(row);
    if (error) {
      // Audit S1 / GAP-7: surface drop to Vercel logs so it isn't silent
      console.error('[logEvent] dropped row', {
        module: row.module, step: row.step, status: row.status,
        error_code: error.code, error_message: error.message,
      });
      return false;
    }
    return true;
  } catch (err) {
    console.error('[logEvent] insert threw', {
      module: row.module, step: row.step, status: row.status,
      message: err?.message,
    });
    return false;
  }
}

// Convenience for tests + callers that want a no-throw boundary
export function logEventSafe(input, options = {}) {
  return Promise.resolve()
    .then(() => logEvent(input, options))
    .catch(() => undefined);
}
